import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { useSubmissions, type SubmissionStatus } from '@/api/edi';
import { useAuthStore } from '@/stores/auth';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { cn } from '@/lib/cn';
import { ediSubmissionVariant } from '@/lib/statusBadge';

import { extractErrorMessage } from '@/lib/extractErrorMessage';
/*
 * Submission history for 837P batches. Shows every upload with high-level
 * status, whether it was test-mode, and counts/totals. Individual claim
 * dispositions live on the detail page.
 */

const STATUS_LABEL: Record<SubmissionStatus, string> = {
    pending_upload: 'Pending upload',
    uploaded:       'Uploaded',
    ack_accepted:   'Accepted (999)',
    ack_rejected:   'Rejected',
    partial_paid:   'Partially paid',
    paid:           'Paid',
    failed:         'Failed',
};

function fmtMoney(n: number): string {
    return n.toLocaleString(undefined, { style: 'currency', currency: 'USD' });
}

function fmtDateTime(iso: string | null): string {
    if (!iso) return '';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleString(undefined, {
        year: 'numeric', month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit',
    });
}

export function BillingSubmissionsPage() {
    const canView = useAuthStore((s) => s.hasPermission('view_billing'));
    const navigate = useNavigate();
    const { data: submissions, isLoading, isError, error } = useSubmissions();

    if (!canView) {
        return (
            <div className="p-6 max-w-[1100px] mx-auto">
                <h1 className="font-display text-2xl font-bold text-text mb-1">Submissions</h1>
                <p className="text-sm text-text-muted">
                    You don't have permission to view claim submissions.
                </p>
            </div>
        );
    }

    return (
        <div className="p-6 max-w-[1400px] mx-auto">
            <div className="flex items-center gap-3 mb-4">
                <Button variant="ghost" size="sm" onClick={() => navigate('/billing')}>
                    <ArrowLeft className="w-3.5 h-3.5" /> Back
                </Button>
                <div>
                    <h1 className="font-display text-2xl font-bold text-text">Submissions</h1>
                    <p className="text-xs text-text-subtle">
                        837P batches uploaded to Office Ally. Click a row to view responses, claim
                        disposition, and download the raw X12 payload.
                    </p>
                </div>
            </div>

            <Card className="p-0 overflow-hidden">
                {isLoading ? (
                    <div className="p-12 flex items-center justify-center text-text-subtle">
                        <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading…
                    </div>
                ) : isError ? (
                    <div className="p-12 text-center text-error">
                        <p className="font-medium mb-1">Couldn't load submissions</p>
                        <p className="text-xs text-text-subtle">
                            {extractErrorMessage(error, 'Please try again.')}
                        </p>
                    </div>
                ) : !submissions || submissions.length === 0 ? (
                    <div className="p-12 text-center text-text-subtle">
                        <p>No submissions yet. Select Ready claims on the Billing page to submit.</p>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-left text-2xs uppercase tracking-wider text-text-subtle border-b border-border">
                                    <th className="px-3 py-2 font-bold">Filename</th>
                                    <th className="px-3 py-2 font-bold">Uploaded</th>
                                    <th className="px-3 py-2 font-bold">ICN</th>
                                    <th className="px-3 py-2 font-bold text-right">Claims</th>
                                    <th className="px-3 py-2 font-bold text-right">Billed</th>
                                    <th className="px-3 py-2 font-bold">Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {submissions.map((s) => (
                                    <tr
                                        key={s.id}
                                        onClick={() => navigate(`/billing/submissions/${s.id}`)}
                                        className="border-b border-border last:border-b-0 hover:bg-surface-muted transition-colors cursor-pointer"
                                    >
                                        <td className="px-3 py-2 whitespace-nowrap">
                                            <Link to={`/billing/submissions/${s.id}`} className="font-mono text-xs hover:underline">
                                                {s.filename}
                                            </Link>
                                            {s.isTest && (
                                                <span className="ml-1.5 text-2xs uppercase tracking-wide text-warning-text">TEST</span>
                                            )}
                                        </td>
                                        <td className="px-3 py-2 text-text-muted whitespace-nowrap">
                                            {fmtDateTime(s.uploadedAt || s.createdAt)}
                                        </td>
                                        <td className="px-3 py-2 text-text-muted font-mono text-xs">
                                            {s.interchangeControl}
                                        </td>
                                        <td className="px-3 py-2 text-right tabular-nums">{s.claimCount}</td>
                                        <td className="px-3 py-2 text-right tabular-nums">{fmtMoney(s.totalBilled)}</td>
                                        <td className="px-3 py-2">
                                            <Badge variant={ediSubmissionVariant(s.status)} className={cn(s.status === 'failed' && 'uppercase')}>
                                                {STATUS_LABEL[s.status] || s.status}
                                            </Badge>
                                            {s.lastError && (
                                                <div className="text-2xs text-error mt-0.5 truncate max-w-xs" title={s.lastError}>
                                                    {s.lastError}
                                                </div>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </Card>
        </div>
    );
}
